import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ArrowRight, Check, Upload, Loader2, X, Copy, Sparkles, FileText, MailCheck, Globe, LogOut, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { BRAND_GUIDE_PROMPTS, ONBOARDING_EXAMPLES, identityMode } from "@/lib/toolRegistry";
import { useOnboardingAutosave } from "@/lib/useOnboardingAutosave";
import { meta } from "@/lib/metaPixel";

const STEPS = [
  { key: "intro", type: "intro" },
  { key: "identity", q: "What is your diving identity?", sub: "Pick the closest to how you show up in the water.", type: "options",
    options: ["Recreational Diver", "Freediver", "Technical Diver", "Dive Instructor", "Dive Centre", "Dive Shop", "Dive Creator", "Dive Brand"] },
  { key: "niche", q: "What is your niche?", type: "text",
    placeholder: "e.g. Buoyancy coaching for technical divers in the Mediterranean" },
  { key: "audience", q: "Who is your audience?", type: "text",
    placeholder: "e.g. Open-water certified divers, 28-45, who want to look effortless on camera" },
  { key: "goals", q: "What are your goals?", sub: "Pick all that apply.", type: "multi",
    options: ["Growth", "Reach", "Traffic", "Leads", "Sales", "Authority", "Brand Deals", "Community", "Retention"] },
  { key: "platform", q: "Primary platform?", type: "options",
    options: ["Instagram", "TikTok", "Facebook", "YouTube Shorts"] },
  { key: "personality", q: "Describe your personality.", type: "text",
    placeholder: "e.g. Calm, peer-to-peer, slightly reflective. I never preach." },
  { key: "desired_action", q: "What action do you want people to take?", type: "options",
    options: ["Follow", "Comment", "DM", "Website", "Free Resource", "Product"] },
  { key: "topics", q: "What topics could you talk about all year?", sub: "Comma-separated. These become your content pillars.", type: "text",
    placeholder: "e.g. Trim, breathing, gear setup, dive psychology, marine life behaviour" },
  { key: "website", q: "What's your website URL?", sub: "Optional but powerful. We'll read your public pages to sharpen every output.", type: "website",
    placeholder: "https://your-dive-centre.com" },
  { key: "examples", q: "Upload examples and an optional brand guide.", sub: "Both steps are optional. Skip if you prefer.", type: "upload" },
];

function VerifyEmailGate({ user, onContinue, onSignOut }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/send-verification-email"); setSent(true); toast.success("Sent - check your inbox"); }
    catch { toast.error("Could not send. Try again in a moment."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="onboarding-verify-gate" className="min-h-screen flex items-center justify-center px-5 bg-[#F8FAFC] relative">
      <button
        onClick={onSignOut}
        data-testid="verify-gate-signout"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#64748B] hover:text-[#B91C1C] px-2.5 py-1.5 rounded-md hover:bg-white transition-colors"
      >
        <LogOut className="w-3.5 h-3.5" />
        <span>Sign out</span>
      </button>
      <div className="dms-card p-8 max-w-lg w-full">
        <div className="w-12 h-12 rounded-full bg-[#4FD1C5]/15 flex items-center justify-center mb-4">
          <MailCheck className="w-5 h-5 text-[#1D4E89]" />
        </div>
        <div className="dms-label">Verify your email first</div>
        <h1 className="mt-2 font-heading font-bold text-2xl text-[#0A2342]">We sent a verification link to</h1>
        <div className="mt-1 font-mono text-[13.5px] text-[#1D4E89] break-all">{user?.email}</div>
        <p className="mt-3 text-[14px] text-[#475569]">
          Click the link in your inbox to activate your Dive Brain and start onboarding. Didn&apos;t get it? Check spam, or resend below.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {sent ? (
            <button disabled className="dms-btn-ghost">Verification email sent</button>
          ) : (
            <button onClick={resend} disabled={busy} data-testid="onboarding-resend" className="dms-btn-primary">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend verification email"}
            </button>
          )}
          <button onClick={onContinue} data-testid="onboarding-continue-anyway" className="dms-btn-ghost">I&apos;ll verify later - continue for now</button>
        </div>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ goals: [] });
  const [exampleFiles, setExampleFiles] = useState([]);
  const [brandGuide, setBrandGuide] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [gateBypass, setGateBypass] = useState(false);
  const [siteFetching, setSiteFetching] = useState(false);
  const [siteResult, setSiteResult] = useState(null);
  const [restoredAt, setRestoredAt] = useState(null);
  const restoredOnceRef = useRef(false);

  // Autosave (localStorage + debounced server draft)
  const snapshotFn = useMemo(
    () => () => ({
      answers,
      step,
      example_file_ids: exampleFiles.map((f) => f.id),
      brand_guide_file_id: brandGuide?.id || null,
      brand_guide_name: brandGuide?.name || null,
      brand_guide_chars: brandGuide?.chars || 0,
    }),
    [answers, step, exampleFiles, brandGuide]
  );
  const { save, readLocal, clearLocal } = useOnboardingAutosave(user?.user_id, snapshotFn);

  // Restore draft on mount — server first (survives cache clear/device switch), then localStorage fallback.
  useEffect(() => {
    if (restoredOnceRef.current || !user?.user_id) return;
    restoredOnceRef.current = true;
    (async () => {
      let draft = null;
      try {
        const { data } = await api.get("/onboarding/draft");
        draft = data?.draft || null;
      } catch (e) { console.debug("draft fetch failed:", e?.response?.status); }
      if (!draft) draft = readLocal();
      if (draft && (draft.answers || draft.step)) {
        if (draft.answers) setAnswers((prev) => ({ ...prev, ...draft.answers, goals: draft.answers.goals || prev.goals || [] }));
        if (typeof draft.step === "number") setStep(Math.max(0, Math.min(draft.step, 100)));
        if (Array.isArray(draft.example_file_ids) && draft.example_file_ids.length > 0) {
          setExampleFiles(draft.example_file_ids.map((id) => ({ id, name: "Uploaded" })));
        }
        if (draft.brand_guide_file_id) {
          setBrandGuide({ id: draft.brand_guide_file_id, name: draft.brand_guide_name || "Brand guide", chars: draft.brand_guide_chars || 0 });
        }
        setRestoredAt(draft.updated_at || draft._saved_at || null);
        toast.success("Welcome back — we restored your progress.");
      }
    })();
  }, [user?.user_id, readLocal]);

  // Persist on every change (debounced inside the hook)
  useEffect(() => {
    if (!user?.user_id) return;
    save();
  }, [answers, step, exampleFiles, brandGuide, user?.user_id, save]);

  // Meta: fire OnboardingStarted the first time the user leaves the intro and
  // actually begins answering questions. `meta.onboardingStarted` uses
  // persistent per-user dedupe so re-visits will not re-fire the event.
  useEffect(() => {
    if (user?.user_id && step >= 1) meta.onboardingStarted(user.user_id);
  }, [user?.user_id, step]);

  const discardDraft = async () => {
    if (!window.confirm("Start over from scratch? Your saved progress will be discarded.")) return;
    clearLocal();
    try { await api.delete("/onboarding/draft"); } catch (e) { console.debug("draft delete failed:", e?.response?.status); }
    setAnswers({ goals: [] });
    setExampleFiles([]);
    setBrandGuide(null);
    setStep(0);
    setRestoredAt(null);
    toast.success("Fresh start. Every field is empty.");
  };

  const needsVerify = user && !user.email_verified && user.auth_provider !== "google" && !gateBypass;
  if (needsVerify) {
    return <VerifyEmailGate user={user} onContinue={() => setGateBypass(true)} onSignOut={async () => { await logout(); nav("/"); }} />;
  }

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;
  const mode = identityMode(answers.identity);
  const activePrompt = BRAND_GUIDE_PROMPTS[mode] || BRAND_GUIDE_PROMPTS.business;
  const dynExamples = ONBOARDING_EXAMPLES[answers.identity] || ONBOARDING_EXAMPLES.default;
  const exampleForCurrent = dynExamples[current.key];
  const weakText = exampleForCurrent?.weak;
  const strongText = exampleForCurrent?.strong;

  const canProceed = (() => {
    if (current.type === "intro" || current.type === "upload" || current.type === "website") return true;
    if (current.type === "options") return !!answers[current.key];
    if (current.type === "multi") return Array.isArray(answers.goals) && answers.goals.length > 0;
    return (answers[current.key] || "").trim().length > 1;
  })();

  const setField = (val) => setAnswers((a) => ({ ...a, [current.key]: val }));
  const toggleGoal = (g) => setAnswers((a) => ({
    ...a, goals: a.goals?.includes(g) ? a.goals.filter((x) => x !== g) : [...(a.goals || []), g],
  }));

  const handleImages = async (files) => {
    if (!files?.length) return;
    if (exampleFiles.length + files.length > 3) { toast.error("Max 3 example images"); return; }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setExampleFiles((arr) => [...arr, { id: data.id, name: f.name }]);
      }
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const handleBrandGuide = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrandGuide({ id: data.id, name: file.name, chars: data.extracted_text_length || 0 });
      if (!data.extracted_text_length) toast.warning("We couldn't extract text from that file - outputs will rely on your other answers.");
      else toast.success(`Extracted ${data.extracted_text_length.toLocaleString()} characters from your brand guide`);
    } catch (e) {
      console.error("Brand guide upload failed:", e);
      toast.error("Brand guide upload failed");
    } finally { setUploading(false); }
  };

  const copyPrompt = async () => {
    try { await navigator.clipboard.writeText(activePrompt); toast.success("Prompt copied"); } catch (err) { console.debug("clipboard write failed", err); toast.error("Clipboard blocked by the browser."); }
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const payload = {
        identity: answers.identity || "",
        niche: answers.niche || "",
        audience: answers.audience || "",
        goals: answers.goals || [],
        platform: answers.platform || "",
        personality: answers.personality || "",
        desired_action: answers.desired_action || "",
        topics: answers.topics || "",
        example_image_ids: exampleFiles.map((f) => f.id),
        brand_guide_file_id: brandGuide?.id || null,
      };
      await api.post("/onboarding/complete", payload);
      clearLocal();
      // Meta: onboarding is a critical activation milestone. We attach a small
      // set of controlled, non-PII categorisation values only — never the raw
      // niche/audience/personality text (that stays server-side).
      if (user?.user_id) {
        meta.onboardingCompleted(user.user_id, {
          business_type: identityMode(answers.identity) || "unknown",
          channels_count: 1, // primary channel picked in step 5
        });
      }
      // If the user supplied a website URL, fetch + store it on the Dive Brain in the background.
      if (answers.website && answers.website.trim()) {
        api.post("/dive-brain/website-intel", { url: answers.website.trim(), store: true }).catch(() => {});
      }
      await refresh();
      toast.success("Your Dive Brain is ready");
      // Route straight to the Dashboard — new UX flow (Feb 2026): the
      // Strategy Operator is now a separate opt-in step from the Dashboard
      // WelcomeSetup card. Forcing users into another 8-section wizard
      // immediately after the Dive Brain 11-step interview was killing
      // completion. Users pick when they're ready.
      nav("/dashboard?fresh=1", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate Dive Brain");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#F8FAFC]">
      <div className="sticky top-0 z-10 bg-[#F8FAFC]/85 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-5 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-7 h-7 rounded-md bg-[#0A2342] flex items-center justify-center">
              <span className="text-[#4FD1C5] font-heading font-bold text-xs">D</span>
            </div>
            <span className="hidden sm:inline font-heading font-bold text-[14px] text-[#0A2342]">Dive Brain</span>
          </div>
          <div className="flex-1">
            <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden">
              <div data-testid="onboarding-progress" className="h-full bg-[#4FD1C5] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#64748B] font-semibold">{step + 1}/{STEPS.length}</div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            data-testid="onboarding-signout"
            title="Sign out"
            aria-label="Sign out"
            className="ml-1 sm:ml-2 inline-flex items-center gap-1.5 text-[11px] sm:text-[12px] font-semibold text-[#64748B] hover:text-[#B91C1C] px-2 py-1 rounded-md hover:bg-[#FEF2F2] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        {restoredAt && (
          <div data-testid="onboarding-restored-banner" className="max-w-3xl mx-auto px-5 pb-2 -mt-1 flex items-center justify-between gap-3 text-[11.5px] text-[#0F766E]">
            <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3" /> Progress restored from {String(restoredAt).slice(0, 10)}</span>
            <button
              data-testid="onboarding-discard-draft"
              onClick={discardDraft}
              className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#64748B] hover:text-[#B91C1C]"
              title="Discard saved progress and start over"
            ><RotateCcw className="w-3 h-3" /> Start over</button>
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14 lg:py-20">
        <div key={current.key} className="animate-fade-up">
          {current.type === "intro" ? (
            <div>
              <div className="dms-label mb-3">Welcome</div>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] leading-tight">Before you answer the next 9 questions.</h1>
              <p className="mt-3 text-[#475569] text-[15.5px] leading-relaxed">
                The Dive Brain is the difference between AI slop and useful, specific marketing. The more detail you give us, the better every output becomes for the next 12 months.
              </p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { t: "Be specific", d: "\"SSI dive centre in Malta\" beats \"diving\"." },
                  { t: "Answer as a person", d: "Talk like you would to a peer, not a marketing form." },
                  { t: "Vague = weak", d: "Vague answers create generic outputs. Detailed answers compound." },
                ].map((c) => (
                  <div key={c.t} className="dms-card p-5">
                    <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Tip</div>
                    <div className="mt-2 font-heading font-bold text-[15px] text-[#0A2342]">{c.t}</div>
                    <p className="mt-1.5 text-[13.5px] text-[#475569] leading-relaxed">{c.d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 dms-card p-5 bg-[#F8FAFC] border-dashed">
                <div className="dms-label mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3" /> Pro tip</div>
                <div className="text-[14.5px] text-[#1F2937] leading-relaxed">
                  Before you start, you can use ChatGPT, Claude or Gemini to generate a detailed brand guide using our prompt - then upload it on the final step for an even sharper Dive Brain.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="dms-label mb-3">Step {step}</div>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] leading-tight">{current.q}</h1>
              {current.sub && <p className="mt-3 text-[#475569] text-[15.5px]">{current.sub}</p>}

              {(weakText || strongText) && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {weakText && (
                    <div className="dms-card p-4 border-l-4 border-l-[#FCA5A5]" data-testid="onboarding-weak">
                      <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#B91C1C]">Weak</div>
                      <div className="mt-1.5 text-[14px] text-[#475569] italic">&ldquo;{weakText}&rdquo;</div>
                    </div>
                  )}
                  {strongText && (
                    <div className="dms-card p-4 border-l-4 border-l-[#4FD1C5]" data-testid="onboarding-strong">
                      <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#0F766E]">Strong · {answers.identity || "example"}</div>
                      <div className="mt-1.5 text-[14px] text-[#1F2937]">&ldquo;{strongText}&rdquo;</div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-7 sm:mt-9">
                {current.type === "options" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {current.options.map((opt) => {
                      const active = answers[current.key] === opt;
                      return (
                        <button
                          key={opt}
                          data-testid={`option-${current.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => setField(opt)}
                          className={`text-left rounded-xl border px-4 py-4 transition-all duration-200 ${
                            active ? "border-[#0A2342] bg-[#0A2342] text-white shadow-[0_6px_18px_rgba(10,35,66,0.18)]" : "border-[#E2E8F0] bg-white hover:border-[#1D4E89]"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{opt}</span>
                            {active && <Check className="w-4 h-4" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {current.type === "multi" && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {current.options.map((opt) => {
                      const active = answers.goals?.includes(opt);
                      return (
                        <button
                          key={opt} type="button"
                          data-testid={`goal-${opt.toLowerCase()}`}
                          onClick={() => toggleGoal(opt)}
                          className={`text-left rounded-xl border px-4 py-3.5 transition-all duration-200 ${
                            active ? "border-[#0A2342] bg-[#0A2342] text-white" : "border-[#E2E8F0] bg-white hover:border-[#1D4E89]"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-[14.5px]">{opt}</span>
                            {active && <Check className="w-4 h-4" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {current.type === "text" && (
                  <textarea
                    data-testid={`input-${current.key}`}
                    value={answers[current.key] || ""}
                    onChange={(e) => setField(e.target.value)}
                    className="dms-textarea text-[16px]" rows={5}
                    placeholder={current.placeholder}
                  />
                )}

                {current.type === "website" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#1D4E89]" />
                      <input
                        data-testid="input-website"
                        type="url" value={answers.website || ""}
                        onChange={(e) => setField(e.target.value)}
                        className="dms-input flex-1 text-[16px]" placeholder={current.placeholder}
                      />
                      <button
                        type="button"
                        data-testid="website-fetch"
                        disabled={!answers.website || siteFetching}
                        onClick={async () => {
                          setSiteFetching(true); setSiteResult(null);
                          try {
                            const { data } = await api.post("/dive-brain/website-intel", { url: answers.website, store: false });
                            setSiteResult(data.intel);
                            toast.success("Fetched. We'll use it to sharpen your Dive Brain.");
                          } catch (e) {
                            toast.error(e?.response?.data?.detail?.error || "Could not fetch. We'll skip it.");
                          } finally { setSiteFetching(false); }
                        }}
                        className="dms-btn-primary"
                      >
                        {siteFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test fetch"}
                      </button>
                    </div>
                    {siteResult && (
                      <div data-testid="website-result" className="dms-card p-4 text-[13px]">
                        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">Preview</div>
                        <div className="mt-1 font-heading font-bold text-[#0A2342]">{siteResult.title || siteResult.url}</div>
                        {siteResult.description && <div className="mt-1 text-[#475569]">{siteResult.description}</div>}
                        <div className="mt-2 text-[11.5px] text-[#94A3B8]">{siteResult.chars || 0} characters extracted</div>
                      </div>
                    )}
                    <p className="text-[12.5px] text-[#94A3B8]">Skip if you don&apos;t have one - you can add it later from your Dive Brain page.</p>
                  </div>
                )}

                {current.type === "upload" && (
                  <div className="space-y-6">
                    <div>
                      <div className="dms-label mb-2">1. Reference content (optional)</div>
                      <label data-testid="upload-dropzone" htmlFor="onboarding-files" className="block cursor-pointer rounded-xl border-2 border-dashed border-[#E2E8F0] bg-white p-8 text-center hover:border-[#4FD1C5] transition">
                        <Upload className="w-6 h-6 mx-auto text-[#1D4E89] mb-3" />
                        <div className="font-semibold text-[#0A2342]">Upload 1-3 examples of content you like</div>
                        <div className="text-xs text-[#64748B] mt-1">PNG / JPG up to 10MB</div>
                        <input id="onboarding-files" data-testid="upload-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImages(Array.from(e.target.files || []))} />
                      </label>
                      {!!exampleFiles.length && (
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          {exampleFiles.map((f) => (
                            <div key={f.id} className="relative">
                              <div className="aspect-square rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center text-xs text-[#64748B] text-center p-2"><span className="truncate">{f.name}</span></div>
                              <button onClick={() => setExampleFiles((arr) => arr.filter((x) => x.id !== f.id))} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center text-[#64748B]"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="dms-divider" />

                    <div>
                      <div className="dms-label mb-2">2. AI Brand Guide (optional, but powerful)</div>
                      <p className="text-[14px] text-[#475569] leading-relaxed">
                        Copy our Brand Kit prompt into your existing ChatGPT, Claude or Gemini chat. It uses everything that AI already knows about you, asks only the questions it genuinely needs, then produces a professionally formatted .docx Brand Guide. Upload it below and every DMS output learns your voice.
                      </p>
                      <div className="mt-2 text-[12.5px] text-[#475569]">
                        The DMS Brand Kit prompt is universal — it works for dive centres, shops, brands, creators and instructors.
                      </div>
                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <button type="button" data-testid="brand-guide-toggle" onClick={() => setShowPrompt(!showPrompt)} className="dms-btn-ghost">
                          <Sparkles className="w-4 h-4" /> {showPrompt ? "Hide" : "Show"} brand guide prompt
                        </button>
                        <button type="button" data-testid="brand-guide-copy" onClick={copyPrompt} className="dms-btn-ghost"><Copy className="w-4 h-4" /> Copy prompt</button>
                      </div>
                      {showPrompt && (
                        <pre data-testid="brand-guide-prompt" className="mt-3 dms-card p-4 text-[13px] leading-relaxed whitespace-pre-wrap text-[#1F2937] max-h-72 overflow-y-auto">{activePrompt}</pre>
                      )}
                      <label htmlFor="brand-guide-file" className="mt-4 block cursor-pointer rounded-xl border-2 border-dashed border-[#E2E8F0] bg-white p-6 text-center hover:border-[#4FD1C5] transition">
                        <FileText className="w-5 h-5 mx-auto text-[#1D4E89] mb-2" />
                        <div className="font-semibold text-[#0A2342] text-[14px]">Upload AI brand guide (PDF / DOCX / TXT)</div>
                        <input id="brand-guide-file" data-testid="brand-guide-input" type="file" accept=".pdf,.docx,.txt,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => handleBrandGuide(e.target.files?.[0])} />
                      </label>
                      {brandGuide && (
                        <div className="mt-3 dms-card p-4 flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-[#0A2342] text-[14px]">{brandGuide.name}</div>
                            <div className="text-xs text-[#64748B]">{brandGuide.chars.toLocaleString()} characters extracted</div>
                          </div>
                          <button data-testid="brand-guide-remove" onClick={() => setBrandGuide(null)} className="text-[#64748B] hover:text-[#0A2342]"><X className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>

                    {uploading && <div className="text-sm text-[#64748B] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</div>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-10 sm:mt-12 flex items-center justify-between">
          <button data-testid="onboarding-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="dms-btn-ghost disabled:opacity-40">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="onboarding-next" onClick={() => setStep((s) => s + 1)} disabled={!canProceed} className="dms-btn-primary">
              {current.type === "intro" ? "Begin" : "Continue"} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="onboarding-finish" onClick={finish} disabled={submitting} className="dms-btn-accent">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <>Generate Dive Brain <ArrowRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
